import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Badge, SectionTitle, Field } from "@/components/ui-hud";
import AirportInput from "@/components/AirportInput";
import { Plane, Search, Upload, Plus, DollarSign, Clock, MapPin, FileText, ExternalLink, X as XIcon, Video, Camera, Loader2, Radar, User as UserIcon, Mail, Phone, Globe, MessageCircle, Pencil, Trash2, Sparkles, Award, Check } from "lucide-react";

/**
 * AircraftSales — broker-focused listings page.
 *
 * Reads from the existing /api/marketplace collection filtered to kind=plane and
 * splits the view into "For Sale" and "Aircraft Wanted" tabs. Only users with a
 * broker/aircraft_sales role (or admins) can post — everyone can browse.
 *
 * Inspired by controller.com — big photo, rich specs, seller card, spec-sheet download.
 */
export default function AircraftSales() {
  const { user } = useAuth();
  const canPost = !!user && (user.is_admin || ["aircraft_sales", "broker", "aircraft_owner", "charter_operator", "operator"].includes(user.role));
  const [tab, setTab] = useState("for_sale");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind: "plane" });
      params.set("listing_type", tab);
      const r = await api.get(`/marketplace?${params.toString()}`);
      let items = r.data || [];
      // Some historical items may not have listing_type; treat undefined as for_sale
      if (tab === "for_sale") {
        items = items.filter((x) => !x.listing_type || x.listing_type === "for_sale");
      } else {
        items = items.filter((x) => x.listing_type === "wanted");
      }
      if (q) {
        const t = q.toLowerCase();
        items = items.filter((x) => (x.title || "").toLowerCase().includes(t) || (x.make || "").toLowerCase().includes(t) || (x.model || "").toLowerCase().includes(t) || (x.location || "").toLowerCase().includes(t));
      }
      setRows(items);
    } catch (e) {
      toast.error("Failed to load listings");
    } finally { setLoading(false); }
  }, [tab, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="aircraft-sales-page">
      <SectionTitle overline="// PJP Marketplace · Aircraft" title="Aircraft for Sale & Wanted"
        subtitle="Private jet listings & buy requests from PJP brokers. Verified sellers, spec-sheet ready, direct-to-seller inquiries." />

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="aircraft-sales-tabs">
        <button data-testid="tab-for-sale" onClick={() => setTab("for_sale")} className={`px-4 py-2 rounded-lg mono text-[11px] tracking-widest uppercase border transition-colors ${tab === "for_sale" ? "bg-sky/15 text-sky border-sky/40" : "text-white/55 border-transparent hover:bg-white/5"}`}>
          <Plane className="inline h-3.5 w-3.5 mr-1.5" />For Sale
        </button>
        <button data-testid="tab-wanted" onClick={() => setTab("wanted")} className={`px-4 py-2 rounded-lg mono text-[11px] tracking-widest uppercase border transition-colors ${tab === "wanted" ? "bg-orange-hud/15 text-orange-hud border-orange-hud/40" : "text-white/55 border-transparent hover:bg-white/5"}`}>
          <Radar className="inline h-3.5 w-3.5 mr-1.5" />Aircraft Wanted
        </button>
        <div className="flex-1" />
        <div className="relative min-w-[260px]">
          <Search className="h-4 w-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search make, model, location…" data-testid="aircraft-sales-search" className="w-full bg-white/[0.03] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-sky/60" />
        </div>
        {canPost && (
          <button onClick={() => setComposerOpen(true)} data-testid="btn-post-listing" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mono text-[11px] tracking-widest bg-sky text-obsidian font-bold hover:brightness-110">
            <Plus className="h-3.5 w-3.5" />
            {tab === "wanted" ? "POST WANTED" : "LIST AIRCRAFT"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-20 grid place-items-center text-white/40"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="py-20 text-center text-white/45">
          <Plane className="h-8 w-8 mx-auto mb-3 text-white/25" />
          <div className="mono text-[11px] tracking-widest">NO {tab === "wanted" ? "WANTED" : "FOR-SALE"} LISTINGS YET</div>
          {canPost && <div className="mt-3 text-sm">Be the first to {tab === "wanted" ? "post a buy request" : "list an aircraft"}.</div>}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((l) => <ListingCard key={l.id} listing={l} onOpen={() => setDetail(l)} />)}
        </div>
      )}

      {detail && (
        <ListingDetail
          listing={detail}
          onClose={() => setDetail(null)}
          onEdit={(l) => { setDetail(null); setEditing(l); }}
          onDeleted={() => { setDetail(null); load(); }}
          onStatusChange={(status) => { setDetail((d) => d ? { ...d, status } : d); load(); }}
        />
      )}
      {composerOpen && <ListingComposer listingType={tab} onClose={() => setComposerOpen(false)} onSaved={() => { setComposerOpen(false); load(); }} />}
      {editing && (
        <ListingComposer
          listingType={editing.listing_type || tab}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ---------------- Listing Card ---------------- */

function ListingCard({ listing, onOpen }) {
  const photo = (listing.photos && listing.photos[0]) || listing.image_url;
  const price = listing.price ? `$${Number(listing.price).toLocaleString()}` : "Make offer";
  const subtitle = [listing.make, listing.model, listing.year].filter(Boolean).join(" ");
  const isSold = listing.status === "sold";
  return (
    <button onClick={onOpen} data-testid={`listing-card-${listing.id}`} className="text-left group">
      <Card className={`!p-0 overflow-hidden hover:border-sky/40 transition-colors ${isSold ? "opacity-85" : ""}`}>
        <div className="aspect-[4/3] bg-white/[0.03] relative overflow-hidden">
          {photo ? (
            <img src={photo} alt={listing.title} className={`w-full h-full object-cover group-hover:scale-[1.02] transition-transform ${isSold ? "grayscale-[35%]" : ""}`} />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/20"><Plane className="h-10 w-10" /></div>
          )}
          {listing.listing_type === "wanted" && (
            <div className="absolute top-3 left-3">
              <Badge tone="danger" pulse>WANTED</Badge>
            </div>
          )}
          {listing.spec_sheet_url && !isSold && (
            <div className="absolute top-3 right-3">
              <span className="mono text-[9px] tracking-widest text-sky bg-obsidian/80 px-2 py-1 rounded">SPEC PDF</span>
            </div>
          )}
          {/* SOLD ribbon overlay */}
          {isSold && (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-obsidian/30 via-transparent to-obsidian/50 pointer-events-none" />
              <div
                data-testid="listing-sold-ribbon"
                className="absolute top-6 -right-10 rotate-45 bg-aog text-white font-display font-bold px-14 py-1.5 mono tracking-[0.25em] text-xs shadow-lg pointer-events-none"
                style={{ boxShadow: "0 4px 20px rgba(255,60,80,0.5)" }}
              >
                SOLD
              </div>
            </>
          )}
        </div>
        <div className="p-4">
          <div className="font-display font-semibold text-white text-lg truncate">{listing.title}</div>
          {subtitle && <div className="text-white/55 text-sm truncate">{subtitle}</div>}
          <div className="mt-3 flex items-center justify-between">
            <div className={`font-display font-bold text-lg ${isSold ? "text-white/45 line-through" : "text-sky"}`}>{price}</div>
            <div className="mono text-[10px] tracking-widest text-white/45">{listing.location || "—"}</div>
          </div>
          {listing.total_time && (
            <div className="mt-2 mono text-[10px] tracking-widest text-white/50">TT {Number(listing.total_time).toLocaleString()}h</div>
          )}
        </div>
      </Card>
    </button>
  );
}

/* ---------------- Detail Modal ---------------- */

function ListingDetail({ listing, onClose, onEdit, onDeleted, onStatusChange }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const photos = listing.photos && listing.photos.length ? listing.photos : (listing.image_url ? [listing.image_url] : []);
  const isOwn = user?.id && listing.posted_by === user.id;
  const isSold = listing.status === "sold";

  const [messaging, setMessaging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingSold, setTogglingSold] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharingSocial, setSharingSocial] = useState(false);

  const shareToSocial = async () => {
    if (!user) { toast.info("Sign in to share"); nav("/login"); return; }
    if (sharingSocial) return;
    setSharingSocial(true);
    try {
      await api.post(`/marketplace/${listing.id}/share-social`, {});
      toast.success("PTT to Hot Mic");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't PTT to Hot Mic");
    } finally { setSharingSocial(false); }
  };

  const contactSeller = async () => {
    if (!user) { toast.info("Sign in to contact the seller"); nav("/login"); return; }
    if (isOwn) { toast.info("This is your listing"); return; }
    if (messaging) return;
    setMessaging(true);
    const opener = `Interested in your ${[listing.year, listing.make, listing.model].filter(Boolean).join(" ") || listing.title} — is it still available?`;
    try {
      await api.post(`/messages/with/${listing.posted_by}`, { text: opener });
      toast.success("Message sent — check your inbox");
      nav(`/inbox?user=${listing.posted_by}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 402 && status !== 403) {
        toast.error(errMsg(e, "Couldn't send message"));
      }
    } finally {
      setMessaging(false);
    }
  };

  const deleteListing = async () => {
    if (!window.confirm("Delete this listing? This can't be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/marketplace/${listing.id}`);
      toast.success("Listing deleted");
      onDeleted && onDeleted();
    } catch (e) {
      toast.error("Delete failed");
    } finally { setDeleting(false); }
  };

  const toggleSold = async () => {
    setTogglingSold(true);
    const nextStatus = isSold ? "available" : "sold";
    try {
      await api.post(`/marketplace/${listing.id}/status`, { status: nextStatus });
      toast.success(nextStatus === "sold" ? "Marked as SOLD" : "Back on the market");
      onStatusChange && onStatusChange(nextStatus);
    } catch { toast.error("Status change failed"); }
    finally { setTogglingSold(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 p-4 overflow-y-auto" onClick={onClose}>
      <div className="max-w-4xl mx-auto my-8 bg-obsidian border border-white/10 rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-white/[0.06]">
          <div>
            <div className="mono text-[10px] tracking-widest text-sky/70 uppercase">// {listing.listing_type === "wanted" ? "AIRCRAFT WANTED" : "AIRCRAFT FOR SALE"}</div>
            <div className="font-display font-semibold text-2xl text-white mt-1">{listing.title}</div>
            <div className="text-white/60 mt-0.5">{[listing.make, listing.model, listing.year].filter(Boolean).join(" ")}</div>
          </div>
          <div className="flex items-center gap-2">
            {isOwn && (
              <>
                <button
                  onClick={toggleSold}
                  disabled={togglingSold}
                  data-testid="listing-detail-toggle-sold"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[10px] tracking-widest rounded-md ${
                    isSold
                      ? "text-avail border border-avail/50 hover:bg-avail/10"
                      : "text-white border border-white/25 hover:bg-white/[0.06]"
                  }`}
                >
                  {togglingSold ? <Loader2 className="h-3 w-3 animate-spin" /> : (isSold ? <Check className="h-3 w-3" /> : <Award className="h-3 w-3" />)}
                  {isSold ? "MARK AVAILABLE" : "MARK AS SOLD"}
                </button>
                <button
                  onClick={() => onEdit && onEdit(listing)}
                  data-testid="listing-detail-edit"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[10px] tracking-widest text-cyan border border-cyan/50 rounded-md hover:bg-cyan/10"
                >
                  <Pencil className="h-3 w-3" /> EDIT
                </button>
                <button
                  onClick={deleteListing}
                  disabled={deleting}
                  data-testid="listing-detail-delete"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[10px] tracking-widest text-aog border border-aog/50 rounded-md hover:bg-aog/10 disabled:opacity-40"
                >
                  {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} DELETE
                </button>
              </>
            )}
            <button onClick={onClose} className="text-white/50 hover:text-white ml-1" data-testid="listing-detail-close"><XIcon className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Media */}
        {photos.length > 0 && (
          <div className="grid grid-cols-2 gap-2 p-2">
            {photos.slice(0, 4).map((p, i) => (
              <div key={i} className={`bg-white/[0.03] overflow-hidden rounded ${i === 0 ? "col-span-2 aspect-[16/9]" : "aspect-square"}`}>
                <img src={p} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="p-5 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            {listing.description && (
              <div>
                <div className="eyebrow-muted mb-2">Description</div>
                <div className="text-white/85 whitespace-pre-wrap text-sm leading-relaxed">{listing.description}</div>
              </div>
            )}
            <div>
              <div className="eyebrow-muted mb-2">Specifications</div>
              <div className="grid grid-cols-2 gap-3">
                <SpecRow label="Year"          value={listing.year} />
                <SpecRow label="Serial #"      value={listing.serial_number} />
                <SpecRow label="Registration"  value={listing.tail_number} mono />
                <SpecRow label="Total Time"    value={listing.total_time ? `${Number(listing.total_time).toLocaleString()} hrs` : null} />
                <SpecRow label="Landings"      value={listing.landings ? Number(listing.landings).toLocaleString() : null} />
                <SpecRow label="Seats"         value={listing.seats} />
                <SpecRow label="Engine Program"value={listing.engine_program} />
                <SpecRow label="APU Program"   value={listing.apu_program} />
                <SpecRow label="Avionics"      value={listing.avionics} />
                <SpecRow label="Interior Yr"   value={listing.interior_year} />
                <SpecRow label="Exterior Yr"   value={listing.exterior_year} />
                <SpecRow label="Condition"     value={listing.condition} />
              </div>
            </div>
            {listing.seller_notes && (
              <div>
                <div className="eyebrow-muted mb-2">Seller Notes</div>
                <div className="text-white/85 whitespace-pre-wrap text-sm leading-relaxed">{listing.seller_notes}</div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Card className="!p-4">
              <div className="eyebrow-muted mb-2">Price</div>
              <div className="font-display font-bold text-3xl text-sky">{listing.price ? `$${Number(listing.price).toLocaleString()}` : "Make offer"}</div>
              <div className="mono text-[10px] text-white/50 tracking-widest mt-2">{listing.currency || "USD"} · {listing.location || "—"}</div>
              <div className="mt-4 space-y-2">
                {listing.spec_sheet_url && (
                  <a href={listing.spec_sheet_url} target="_blank" rel="noreferrer" data-testid="listing-detail-spec-pdf" className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 mono text-[11px] tracking-widest text-sky border border-sky/40 hover:bg-sky/10">
                    <FileText className="h-3.5 w-3.5" /> DOWNLOAD SPEC PDF
                  </a>
                )}
                <button
                  onClick={() => { if (!user) { toast.info("Sign in to customize"); nav("/login"); return; } setBrandOpen(true); }}
                  data-testid="listing-detail-brand-spec"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 mono text-[11px] tracking-widest text-cyan border border-cyan/40 hover:bg-cyan/10"
                >
                  <Sparkles className="h-3.5 w-3.5" /> CUSTOMIZE SPEC DATA
                </button>
                <button
                  onClick={() => { if (!user) { toast.info("Sign in to share"); nav("/login"); return; } setShareOpen(true); }}
                  data-testid="listing-detail-share-email"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 mono text-[11px] tracking-widest text-white/80 border border-white/[0.15] hover:bg-white/[0.05] hover:text-white"
                >
                  <Mail className="h-3.5 w-3.5" /> SHARE VIA EMAIL
                </button>
                <button
                  onClick={shareToSocial}
                  disabled={sharingSocial}
                  data-testid="listing-detail-share-social"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 mono text-[11px] tracking-widest text-white/80 border border-white/[0.15] hover:bg-white/[0.05] hover:text-white disabled:opacity-40"
                >
                  {sharingSocial ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radar className="h-3.5 w-3.5" />}
                  PTT TO HOT MIC
                </button>
              </div>
            </Card>

            {/* Seller card — company data + Message the plane */}
            <Card className="!p-4" data-testid="listing-seller-card">
              <div className="eyebrow-muted mb-3">Seller</div>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-white/[0.06] overflow-hidden shrink-0 flex items-center justify-center">
                  {listing.posted_by_avatar_url
                    ? <img src={listing.posted_by_avatar_url} alt="" className="h-full w-full object-cover" />
                    : <UserIcon className="h-4 w-4 text-white/50" />}
                </div>
                <div className="min-w-0 flex-1">
                  <Link to={`/u/${listing.posted_by}`} className="text-white text-sm font-medium hover:text-cyan truncate block" data-testid="listing-seller-name">
                    {listing.posted_by_name || "PJP Member"}
                  </Link>
                  {listing.seller_company && (
                    <div className="text-[12px] text-cyan/85 truncate" data-testid="listing-seller-company">{listing.seller_company}</div>
                  )}
                  {listing.posted_by_role && (
                    <div className="mono text-[9px] tracking-widest text-white/40 uppercase mt-0.5">{(listing.posted_by_role || "").replace(/_/g, " ")}</div>
                  )}
                </div>
              </div>

              <div className="mt-4 space-y-1.5">
                {listing.seller_phone && (
                  <a href={`tel:${listing.seller_phone}`} className="flex items-center gap-2 text-[12px] text-white/75 hover:text-white" data-testid="listing-seller-phone">
                    <Phone className="h-3.5 w-3.5 text-cyan/80" /> {listing.seller_phone}
                  </a>
                )}
                {listing.seller_email && (
                  <a href={`mailto:${listing.seller_email}?subject=Interested%20in%20${encodeURIComponent(listing.title)}`} className="flex items-center gap-2 text-[12px] text-white/75 hover:text-white truncate" data-testid="listing-seller-email">
                    <Mail className="h-3.5 w-3.5 text-cyan/80 shrink-0" /> <span className="truncate">{listing.seller_email}</span>
                  </a>
                )}
                {listing.seller_website && (
                  <a href={/^https?:\/\//i.test(listing.seller_website) ? listing.seller_website : `https://${listing.seller_website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[12px] text-white/75 hover:text-white truncate" data-testid="listing-seller-website">
                    <Globe className="h-3.5 w-3.5 text-cyan/80 shrink-0" /> <span className="truncate">{listing.seller_website}</span>
                  </a>
                )}
              </div>

              <button
                onClick={contactSeller}
                disabled={isOwn || messaging || isSold}
                data-testid="listing-message-seller"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-[12px] font-medium bg-cyan/[0.18] border border-cyan text-white hover:bg-cyan/25 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_16px_rgba(75,156,255,0.25)]"
              >
                {messaging ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : isSold ? (
                  <><Check className="h-4 w-4" /> This aircraft is sold</>
                ) : (
                  <><MessageCircle className="h-4 w-4" /> {isOwn ? "This is your listing" : "Message the plane"}</>
                )}
              </button>
              <div className="mono text-[9px] tracking-widest text-white/35 text-center mt-2">
                REPLIES ARRIVE IN YOUR PJP INBOX
              </div>
            </Card>

            {listing.videos && listing.videos.length > 0 && (
              <Card className="!p-4">
                <div className="eyebrow-muted mb-2">Videos</div>
                {listing.videos.map((v, i) => (
                  <a key={i} href={v} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sky text-sm hover:text-white mono">
                    <Video className="h-3.5 w-3.5" /> Video {i + 1} <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </Card>
            )}
          </div>
        </div>
      </div>
      {brandOpen && (
        <BrandedSpecModal
          listing={listing}
          onClose={() => setBrandOpen(false)}
        />
      )}
      {shareOpen && (
        <ShareEmailModal
          listing={listing}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

/* ---------------- Share via Email Modal ---------------- */

function ShareEmailModal({ listing, onClose }) {
  const { user } = useAuth();
  const senderName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const [form, setForm] = useState({
    to_email: "",
    to_name: "",
    message: `Hi — thought you'd like this ${[listing.year, listing.make, listing.model].filter(Boolean).join(" ") || listing.title}. Let me know if you want to take a closer look.\n\n— ${senderName}`,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async () => {
    if (!form.to_email.trim() || !/^\S+@\S+\.\S+$/.test(form.to_email)) return toast.error("Please enter a valid email");
    setBusy(true);
    try {
      await api.post(`/marketplace/${listing.id}/share-email`, form);
      toast.success(`Sent to ${form.to_email}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send email");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-start justify-center pt-16 p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="w-full max-w-md bg-obsidian border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-email-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
          <div>
            <div className="eyebrow">// SHARE LISTING</div>
            <div className="font-display text-xl">Email this to a friend</div>
            <div className="text-[12px] text-white/55 mt-1">
              We'll send them a beautiful preview of <span className="text-white/85">{listing.title}</span> —
              replies come straight to your inbox.
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><XIcon className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Recipient email *">
            <input
              type="email"
              className="input"
              data-testid="share-email-to"
              value={form.to_email}
              onChange={(e) => set("to_email", e.target.value)}
              placeholder="friend@example.com"
              autoFocus
            />
          </Field>
          <Field label="Their name (optional)">
            <input
              className="input"
              data-testid="share-email-to-name"
              value={form.to_name}
              onChange={(e) => set("to_name", e.target.value)}
              placeholder="Alex"
            />
          </Field>
          <Field label="Personal note">
            <textarea
              className="input"
              rows={5}
              data-testid="share-email-message"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-white/[0.06]">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={send}
            disabled={busy}
            data-testid="share-email-send"
            className="btn"
            style={{ background: "#4B9CFF", color: "#0A1220" }}
          >
            {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</> : <><Mail className="h-3.5 w-3.5" /> Send email</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Branded Spec Modal ---------------- */

function BrandedSpecModal({ listing, onClose }) {
  const { user } = useAuth();
  const rp = user?.role_profile || {};
  const [form, setForm] = useState({
    presenter_name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
    presenter_company:
      rp.company_name || rp.brokerage || rp.sales_company || rp.operator_name || "",
    presenter_email: user?.email || "",
    presenter_phone: user?.phone || rp.phone || "",
    presenter_website: rp.website || "",
    presenter_tagline: "",
    presenter_logo_url: user?.avatar_url || "",
  });
  const [busy, setBusy] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "logo");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("presenter_logo_url", r.data.url);
      toast.success("Logo added");
    } catch (e) { toast.error("Logo upload failed"); }
    finally { setUploadingLogo(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const tok = localStorage.getItem("pjp_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/marketplace/${listing.id}/branded-spec.pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      const clean = (form.presenter_company || form.presenter_name || "PJP").replace(/[^A-Za-z0-9]+/g, "_");
      a.download = `${clean}-${(listing.title || "Aircraft").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
      toast.success("Branded PDF downloaded");
      onClose();
    } catch { toast.error("PDF download failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-start justify-center pt-10 p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-obsidian border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="branded-spec-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
          <div>
            <div className="eyebrow">// CUSTOMIZE SPEC DATA</div>
            <div className="font-display text-xl">Your branded spec sheet</div>
            <div className="text-[12px] text-white/55 mt-1">
              We'll build a PJP-format PDF for <span className="text-white/85">{listing.title}</span> with your
              name, company, and logo on the cover — perfect for sending to a client.
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><XIcon className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Your name">
              <input className="input" data-testid="brand-name" value={form.presenter_name} onChange={(e) => set("presenter_name", e.target.value)} />
            </Field>
            <Field label="Company">
              <input className="input" data-testid="brand-company" value={form.presenter_company} onChange={(e) => set("presenter_company", e.target.value)} placeholder="Integra Jet Management" />
            </Field>
            <Field label="Phone">
              <input className="input" data-testid="brand-phone" value={form.presenter_phone} onChange={(e) => set("presenter_phone", e.target.value)} placeholder="561-555-1234" />
            </Field>
            <Field label="Email">
              <input className="input" data-testid="brand-email" value={form.presenter_email} onChange={(e) => set("presenter_email", e.target.value)} />
            </Field>
            <Field label="Website">
              <input className="input" data-testid="brand-website" value={form.presenter_website} onChange={(e) => set("presenter_website", e.target.value)} placeholder="flyintegra.com" />
            </Field>
            <Field label="Tagline (optional)">
              <input className="input" data-testid="brand-tagline" value={form.presenter_tagline} onChange={(e) => set("presenter_tagline", e.target.value)} placeholder="Turn-key charter since 2011" />
            </Field>
          </div>

          <Field label="Logo (optional)">
            <div className="flex items-center gap-3">
              {form.presenter_logo_url ? (
                <img src={form.presenter_logo_url} alt="logo" className="h-12 w-12 object-contain rounded bg-white/[0.06] border border-white/[0.08] p-1" data-testid="brand-logo-preview" />
              ) : (
                <div className="h-12 w-12 rounded bg-white/[0.03] border border-dashed border-white/20 flex items-center justify-center text-white/30 text-[10px] mono">LOGO</div>
              )}
              <label className="btn btn-sm cursor-pointer">
                <Upload className="h-3.5 w-3.5" /> {uploadingLogo ? "Uploading…" : (form.presenter_logo_url ? "Replace" : "Upload")}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="brand-logo-file" />
              </label>
              {form.presenter_logo_url && (
                <button
                  onClick={() => set("presenter_logo_url", "")}
                  className="text-white/45 hover:text-aog text-[11px] mono"
                  data-testid="brand-logo-clear"
                >REMOVE</button>
              )}
            </div>
          </Field>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/[0.06]">
          <button onClick={onClose} className="btn-ghost" data-testid="brand-cancel">Cancel</button>
          <button
            onClick={download}
            disabled={busy}
            data-testid="brand-download"
            className="btn"
            style={{ background: "#4B9CFF", color: "#0A1220" }}
          >
            {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Building PDF…</> : <><FileText className="h-3.5 w-3.5" /> Download PDF</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function SpecRow({ label, value, mono }) {
  return (
    <div>
      <div className="mono text-[10px] tracking-[0.16em] text-white/45 uppercase">{label}</div>
      <div className={`text-white text-sm ${mono ? "mono text-sky" : ""}`}>{value || "—"}</div>
    </div>
  );
}

/* ---------------- Composer ---------------- */

function ListingComposer({ listingType, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const { user } = useAuth();
  const rp = user?.role_profile || {};
  const sellerCompany =
    rp.company_name || rp.brokerage || rp.sales_company || rp.operator_name || "";
  const missingCompany = !sellerCompany;
  const [form, setForm] = useState(() => existing ? {
    kind: existing.kind || "plane",
    listing_type: existing.listing_type || listingType,
    title: existing.title || "",
    make: existing.make || "",
    model: existing.model || "",
    year: existing.year || "",
    price: existing.price || "",
    location: existing.location || "",
    tail_number: existing.tail_number || "",
    serial_number: existing.serial_number || "",
    total_time: existing.total_time || "",
    landings: existing.landings || "",
    engine_program: existing.engine_program || "",
    apu_program: existing.apu_program || "",
    avionics: existing.avionics || "",
    interior_year: existing.interior_year || "",
    exterior_year: existing.exterior_year || "",
    seats: existing.seats || "",
    condition: existing.condition || "",
    description: existing.description || "",
    seller_notes: existing.seller_notes || "",
    photos: existing.photos || [],
    videos: existing.videos || [],
    spec_sheet_url: existing.spec_sheet_url || "",
  } : {
    kind: "plane",
    listing_type: listingType,
    title: "",
    make: "",
    model: "",
    year: "",
    price: "",
    location: "",
    tail_number: "",
    serial_number: "",
    total_time: "",
    landings: "",
    engine_program: "",
    apu_program: "",
    avionics: "",
    interior_year: "",
    exterior_year: "",
    seats: "",
    condition: "",
    description: "",
    seller_notes: "",
    photos: [],
    videos: [],
    spec_sheet_url: "",
  });  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [catalog, setCatalog] = useState({});

  useEffect(() => { api.get("/faa/type-ratings/catalog").then((r) => setCatalog(r.data || {})).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMake = (v) => setForm((f) => ({ ...f, make: v, model: "" })); // reset model when make changes

  const uploadFile = async (file, kind) => {
    // kind: "photo" | "video" | "spec"
    setUploadingKey(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind === "photo" ? "aircraft-photo" : kind === "video" ? "aircraft-video" : "aircraft-spec");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.url;
      if (!url) throw new Error("Upload response missing URL");
      const absUrl = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
      if (kind === "photo") setForm((f) => ({ ...f, photos: [...(f.photos || []), absUrl] }));
      else if (kind === "video") setForm((f) => ({ ...f, videos: [...(f.videos || []), absUrl] }));
      else if (kind === "spec") set("spec_sheet_url", absUrl);
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (!form.description.trim()) return toast.error("Description required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        price: form.price ? Number(form.price) : null,
        total_time: form.total_time ? Number(form.total_time) : null,
        landings: form.landings ? Number(form.landings) : null,
        interior_year: form.interior_year ? Number(form.interior_year) : null,
        exterior_year: form.exterior_year ? Number(form.exterior_year) : null,
        seats: form.seats ? Number(form.seats) : null,
      };
      if (isEdit) {
        await api.patch(`/marketplace/${existing.id}`, payload);
        toast.success("Listing updated");
      } else {
        await api.post("/marketplace", payload);
        toast.success(`Listing ${listingType === "wanted" ? "posted" : "published"}`);
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(isEdit ? "Failed to update listing" : "Failed to save listing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 p-4 overflow-y-auto" onClick={onClose}>
      <div className="max-w-3xl mx-auto my-8 bg-obsidian border border-white/10 rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
          <div>
            <div className="eyebrow">// {isEdit ? "Edit Listing" : (listingType === "wanted" ? "Post Buy Request" : "List Aircraft")}</div>
            <div className="font-display text-xl">{isEdit ? "Edit Your Listing" : (listingType === "wanted" ? "Aircraft Wanted" : "New Aircraft Listing")}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><XIcon className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Posting-as identity card — snapshotted onto the listing */}
          <div className="flex items-center gap-3 p-3 rounded-lg border border-cyan/25 bg-cyan/[0.04]" data-testid="composer-posting-as">
            <div className="h-10 w-10 rounded-full bg-white/[0.06] overflow-hidden shrink-0 flex items-center justify-center">
              {user?.avatar_url
                ? <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
                : <UserIcon className="h-4 w-4 text-white/50" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="mono text-[9px] tracking-widest text-cyan/80">POSTING AS</div>
              <div className="text-sm text-white truncate">
                {user?.first_name} {user?.last_name}
                {sellerCompany && (
                  <span className="text-white/60"> · {sellerCompany}</span>
                )}
              </div>
              {missingCompany && (user?.role === "aircraft_sales" || user?.role === "broker" || user?.role === "charter_operator") && (
                <div className="text-[11px] text-orange/80 mt-0.5">
                  Add your company name in your <Link to="/profile" className="underline decoration-dotted">profile</Link> — it'll show up on every listing.
                </div>
              )}
            </div>
          </div>

          <Field label={listingType === "wanted" ? "What are you looking for? *" : "Listing title *"}>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={listingType === "wanted" ? "e.g. 2015+ G550 with ADS-B/RVSM/CPDLC" : "e.g. 2018 Gulfstream G550 · Pristine · SN 5555"} data-testid="listing-title" className="input" />
          </Field>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Make">
              <select
                value={form.make}
                onChange={(e) => setMake(e.target.value)}
                data-testid="listing-make"
                className="input"
              >
                <option value="">Select make…</option>
                {Object.keys(catalog).sort((a, b) => a.localeCompare(b)).map((oem) => (
                  <option key={oem} value={oem}>{oem}</option>
                ))}
              </select>
            </Field>
            <Field label="Model">
              <select
                value={form.model}
                onChange={(e) => set("model", e.target.value)}
                data-testid="listing-model"
                className="input"
                disabled={!form.make}
              >
                <option value="">{form.make ? "Select model…" : "Pick a make first"}</option>
                {[...(catalog[form.make] || [])]
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((t) => <option key={t.code} value={t.label}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Year"><input value={form.year} onChange={(e) => set("year", e.target.value)} inputMode="numeric" pattern="^[0-9]*$" className="input" /></Field>
            <Field label={listingType === "wanted" ? "Budget (USD)" : "Price (USD)"}><input value={form.price} onChange={(e) => set("price", e.target.value)} inputMode="numeric" pattern="^[0-9]*$" data-testid="listing-price" className="input" /></Field>
            <Field label="Location">
              <AirportInput
                testId="listing-location"
                value={form.location}
                onChange={(v) => set("location", v)}
                placeholder="Type city or ICAO (KTEB, Teterboro NJ…)"
              />
            </Field>
            <Field label="Condition"><select value={form.condition} onChange={(e) => set("condition", e.target.value)} className="input"><option value="">Select…</option><option>New</option><option>Excellent</option><option>Very Good</option><option>Good</option></select></Field>
          </div>

          {listingType !== "wanted" && (
            <>
              <div className="eyebrow mt-4">Specs</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Registration"><input value={form.tail_number} onChange={(e) => set("tail_number", e.target.value.toUpperCase())} placeholder="N123AB" className="input mono" /></Field>
                <Field label="Serial #"><input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} className="input" /></Field>
                <Field label="Total Time (hrs)"><input value={form.total_time} onChange={(e) => set("total_time", e.target.value)} inputMode="numeric" className="input" /></Field>
                <Field label="Landings"><input value={form.landings} onChange={(e) => set("landings", e.target.value)} inputMode="numeric" className="input" /></Field>
                <Field label="Seats"><input value={form.seats} onChange={(e) => set("seats", e.target.value)} inputMode="numeric" className="input" /></Field>
                <Field label="Engine Program"><input value={form.engine_program} onChange={(e) => set("engine_program", e.target.value)} placeholder="ESP Gold" className="input" /></Field>
                <Field label="APU Program"><input value={form.apu_program} onChange={(e) => set("apu_program", e.target.value)} placeholder="MSP Gold" className="input" /></Field>
                <Field label="Avionics"><input value={form.avionics} onChange={(e) => set("avionics", e.target.value)} placeholder="Honeywell PlaneView" className="input" /></Field>
                <Field label="Interior year"><input value={form.interior_year} onChange={(e) => set("interior_year", e.target.value)} inputMode="numeric" className="input" /></Field>
                <Field label="Exterior year"><input value={form.exterior_year} onChange={(e) => set("exterior_year", e.target.value)} inputMode="numeric" className="input" /></Field>
              </div>
            </>
          )}

          <Field label="Description *">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={5} data-testid="listing-description" className="input" />
          </Field>

          {listingType !== "wanted" && (
            <Field label="Seller notes (private)">
              <textarea value={form.seller_notes} onChange={(e) => set("seller_notes", e.target.value)} rows={3} className="input" />
            </Field>
          )}

          {/* Media */}
          <div className="grid md:grid-cols-3 gap-3">
            <UploadTile label="Add photo" icon={Camera} accept="image/*" uploading={uploadingKey === "photo"} onFile={(f) => uploadFile(f, "photo")} count={form.photos.length} />
            <UploadTile label="Add video URL" icon={Video} manual onManualUrl={(u) => set("videos", [...(form.videos || []), u])} count={form.videos.length} />
            <UploadTile label="Spec sheet PDF" icon={FileText} accept="application/pdf" uploading={uploadingKey === "spec"} onFile={(f) => uploadFile(f, "spec")} done={!!form.spec_sheet_url} />
          </div>

          {/* Photo previews with remove + reorder */}
          {form.photos.length > 0 && (
            <div>
              <div className="eyebrow-muted mb-2">Photos ({form.photos.length})</div>
              <div className="grid grid-cols-4 gap-2" data-testid="composer-photos-grid">
                {form.photos.map((url, i) => (
                  <div key={`${url}-${i}`} className="relative group aspect-square rounded overflow-hidden bg-white/[0.03] border border-white/[0.08]" data-testid={`composer-photo-${i}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {i === 0 && (
                      <div className="absolute top-1 left-1 mono text-[8px] tracking-widest bg-cyan/90 text-obsidian px-1.5 py-0.5 rounded">COVER</div>
                    )}
                    <div className="absolute inset-x-1 bottom-1 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setForm((f) => {
                          if (i === 0) return f;
                          const next = [...f.photos];
                          [next[i - 1], next[i]] = [next[i], next[i - 1]];
                          return { ...f, photos: next };
                        })}
                        disabled={i === 0}
                        title="Move up (make cover)"
                        data-testid={`composer-photo-up-${i}`}
                        className="text-white bg-black/60 rounded px-1.5 py-0.5 text-[10px] disabled:opacity-30"
                      >↑</button>
                      <button
                        onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                        title="Remove"
                        data-testid={`composer-photo-remove-${i}`}
                        className="text-white bg-aog/80 rounded px-1.5 py-0.5 text-[10px]"
                      >✕</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-white/45 mt-2">First photo is the listing cover. Hover a photo to reorder or remove it.</div>
            </div>
          )}

          {/* Videos list */}
          {form.videos.length > 0 && (
            <div>
              <div className="eyebrow-muted mb-2">Videos ({form.videos.length})</div>
              <div className="space-y-1" data-testid="composer-videos-list">
                {form.videos.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px]">
                    <ExternalLink className="h-3 w-3 text-cyan/70" />
                    <a href={v} target="_blank" rel="noreferrer" className="text-cyan hover:text-white truncate flex-1">{v}</a>
                    <button
                      onClick={() => setForm((f) => ({ ...f, videos: f.videos.filter((_, j) => j !== i) }))}
                      data-testid={`composer-video-remove-${i}`}
                      className="text-white/40 hover:text-aog"
                    ><XIcon className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mono text-[10px] text-white/45 pt-2 border-t border-white/[0.06]">
            <b>Coming soon:</b> auto-generated PDF spec sheet from these fields, controller.com-style. For now upload your own PDF above.
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/[0.06]">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="listing-submit" className="btn" style={{ background: "#4B9CFF", color: "#0A1220" }}>
            {saving ? "Saving…" : (isEdit ? "Save changes" : (listingType === "wanted" ? "Post Wanted" : "Publish Listing"))}
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadTile({ label, icon: Icon, accept, uploading, onFile, done, manual, onManualUrl, count }) {
  const [manualUrl, setManualUrl] = useState("");
  if (manual) {
    return (
      <div className="rounded-lg border border-dashed border-white/15 p-3">
        <div className="flex items-center gap-2 text-white/80 mb-2"><Icon className="h-4 w-4 text-sky" /><span className="text-sm">{label}</span></div>
        <div className="flex gap-2">
          <input value={manualUrl} onChange={(e) => setManualUrl(e.target.value)} placeholder="https://…" className="flex-1 bg-white/[0.03] border border-white/10 rounded px-2 py-1.5 text-xs text-white" />
          <button onClick={() => { if (manualUrl) { onManualUrl(manualUrl); setManualUrl(""); } }} className="mono text-[10px] tracking-widest text-sky hover:text-white">ADD</button>
        </div>
        {count > 0 && <div className="mono text-[10px] tracking-widest text-avail mt-1">{count} added</div>}
      </div>
    );
  }
  return (
    <label className="rounded-lg border border-dashed border-white/15 p-3 hover:border-sky/40 cursor-pointer block">
      <div className="flex items-center gap-2 text-white/80 mb-1"><Icon className="h-4 w-4 text-sky" /><span className="text-sm">{label}</span></div>
      <div className="mono text-[10px] tracking-widest text-white/50">{uploading ? "UPLOADING…" : done ? "✓ UPLOADED" : count > 0 ? `${count} added · click to add more` : "CLICK TO UPLOAD"}</div>
      <input type="file" accept={accept} onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} className="hidden" />
    </label>
  );
}
